import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import useGetData from 'hooks/useGetData'
import useChannel from 'hooks/useChannel'
import useSaga from 'hooks/useSaga'
import { FormattedMessage } from 'react-intl'
import { PermissionLevels } from 'utils/permission'
import layoutUsersChannelData from './layoutUsersChannelData'
import PermissionGroup from './PermissionGroup'
import { Waypoint } from 'react-waypoint'
import Loader from 'components/Loader'

export function LayoutUsers ({
  layoutId,

  addUsersArray,
  addUserGroupArray,
}) {
  useChannel(`homeLayout-${layoutId}`)
  const [isLoaded, setIsLoaded] = useState(false)
  const [users, setUsers] = useState([])
  const [cursor, setCursor] = useState(null)
  const [userGroups, setUserGroups] = useState([])

  const prevUserGroupsDataRef = useRef(null)

  const useGroupsArgs = useMemo(() => ({
    layoutId
  }), [layoutId])
  const {
    data: UserGroupsData
  } = useGetData('UserGroupsInHomeLayout', useGroupsArgs)

  useEffect(() => {
    if (
      UserGroupsData &&
      UserGroupsData !== prevUserGroupsDataRef.current
    ) {
      addUserGroupArray(UserGroupsData.Groups.map(UserGroup => ({
        id: UserGroup.Id,
        name: UserGroup.Name
      })))

      setUserGroups([
        ...userGroups.filter(_UserGroup =>
          !UserGroupsData.Groups.find(UserGroup => UserGroup.Id === _UserGroup.Id)
        ),
        ...UserGroupsData.Groups.map(UserGroup => ({
          Id: UserGroup.Id,
          Name: UserGroup.Name,
          PermissionLevel: UserGroup.PermissionLevel,
        }))
      ])

      setUserGroups(UserGroupsData.Groups)
    }

    prevUserGroupsDataRef.current = UserGroupsData
  }, [userGroups, UserGroupsData, addUserGroupArray])

  const handleChannelDataRef = useRef(null)

  useEffect(() => {
    handleChannelDataRef.current = ({ actionName, elementName, data }) => {
      if (actionName + elementName === 'AddUserToHomeLayout/Data/MyHomeLayoutUser') {
        setUsers([
          ...users.filter(User => User.Id !== data.UserId),
          {
            Id: data.UserId,
            PermissionLevel: data.PermissionLevel
          }
        ])
      } else if (actionName + elementName === 'RemoveUserFromHomeLayout/Data/HomeLayoutUser') {
        setUsers(users.filter(User => User.Id !== data.UserId))
      } else if (actionName + elementName === 'SetUserGroupPermissionInHomeLayout/Data/UserGroupInHomeLayout') {
        addUserGroupArray([{
          id: data.GroupId,
          name: data.Name,
        }])

        setUserGroups([
          ...userGroups.filter(UserGroup => UserGroup.Id !== data.GroupId),
          ...(data.PermissionLevel > 0 ? [{
            Id: data.GroupId,
            Name: data.Name,
            PermissionLevel: data.PermissionLevel,
          }] : [])
        ])
      }
    }
  }, [users, userGroups, addUserGroupArray])

  const handleChannelData = useCallback(
    (...args) => {
      handleChannelDataRef.current && handleChannelDataRef.current(...args)
    },
    []
  )

  useSaga(layoutUsersChannelData, layoutId, handleChannelData)

  const prevUsersDataRef = useRef(null)

  const args = useMemo(() => ({
    layoutId,
    cursor,
    internal: true,
    limit: 20
  }), [layoutId, cursor])
  const {
    data,
    isLoading
  } = useGetData('HomeLayoutUsers', args)

  useEffect(() => {
    if (
      data &&
      data !== prevUsersDataRef.current
    ) {
      setUsers([
        ...users,
        ...data.Users
      ])

      addUsersArray(data.Users.map(User => ({
        id: User.Id,
        name: User.Name,
        email: User.Email,
        profileKey: User.ProfileKey,
        licenseType: User.LicenseType,
        isActive: User.Active,
        isLoaded: true
      })))

      if (!data.Cursor) {
        setIsLoaded(true)
      }
    }

    prevUsersDataRef.current = data
  }, [data, users, addUsersArray])

  const handleLoadMore = useCallback(
    () => {
      data && data.Cursor && setCursor(data.Cursor)
    },
    [data]
  )

  const [userGroupOwners, userGroupVisitors] = useMemo(() => {
    const owners = []
    const visitors = []

    userGroups.forEach(UserGroup => {
      if (UserGroup.PermissionLevel === PermissionLevels.OWNER) {
        owners.push(UserGroup)
      }

      if (UserGroup.PermissionLevel === PermissionLevels.VISITOR) {
        visitors.push(UserGroup)
      }
    })

    return [owners, visitors]
  }, [userGroups])

  const [userOwners, userVisitors] = useMemo(() => {
    const owners = []
    const visitors = []

    users.forEach(User => {
      if (User.PermissionLevel === PermissionLevels.OWNER) {
        owners.push(User)
      }

      if (User.PermissionLevel === PermissionLevels.VISITOR) {
        visitors.push(User)
      }
    })

    return [owners, visitors]
  }, [users])

  return (
    <React.Fragment>
      {(UserGroupsData && data) &&
        <React.Fragment>
          <PermissionGroup
            layoutId={layoutId}
            permissionLevel={PermissionLevels.OWNER}
            title={<FormattedMessage id='DESK_OWNERS' />}
            userGroups={userGroupOwners}
            users={userOwners}
            isLoaded={isLoaded}
          />
          <PermissionGroup
            layoutId={layoutId}
            permissionLevel={PermissionLevels.VISITOR}
            title={<FormattedMessage id='DESK_VISITORS' />}
            userGroups={userGroupVisitors}
            users={userVisitors}
            isLoaded={isLoaded}
          />
        </React.Fragment>
      }
      {(!isLoaded && !isLoading) &&
        <Waypoint
          onEnter={handleLoadMore}
          bottomOffset='-50%'
        />
      }
      {isLoading &&
        <Loader
          center
        />
      }
    </React.Fragment>
  )
}

LayoutUsers.propTypes = {
  layoutId: PropTypes.number.isRequired,

  addUsersArray: PropTypes.func.isRequired,
  addUserGroupArray: PropTypes.func.isRequired,
}

export default LayoutUsers
